import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import Page from "./Page";
import useExpenseAccount from "../../hooks/useExpenseAccount";

const DEFAULT_FORM = {
  id: null,
  name: "",
  name_fr: "",
  type: "expense",
  glAccount: "",
  sortOrder: 0,
  isActive: true,
};

const ExpenseAccounts = () => {
  const {
    loading,
    expenseAccounts,
    loadExpenseAccounts,
    saveExpenseAccount,
    removeExpenseAccount,
  } = useExpenseAccount();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState(DEFAULT_FORM);

  useEffect(() => {
    loadExpenseAccounts();
  }, []);

  const openCreate = () => {
    setFormData(DEFAULT_FORM);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setFormData({
      id: row.id,
      name: row.name || "",
      name_fr: row.name_fr || "",
      type: row.type || "expense",
      glAccount: row.glAccount || "",
      sortOrder: Number(row.sortOrder || 0),
      isActive: row.isActive !== false,
    });
    setDialogOpen(true);
  };

  const onSave = async () => {
    const ok = await saveExpenseAccount(formData);
    if (ok) {
      setDialogOpen(false);
      setFormData(DEFAULT_FORM);
    }
  };

  return (
    <Page pageName="expenseAccounts">
      <Stack spacing={2}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6">Expense Accounts</Typography>
          <Button
            variant="contained"
            onClick={openCreate}
          >
            Add Account
          </Button>
        </Box>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>French Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>GL Account</TableCell>
              <TableCell>Sort</TableCell>
              <TableCell>Active</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(expenseAccounts || []).map((row) => (
              <TableRow
                key={row.id}
                hover
              >
                <TableCell>{row.name}</TableCell>
                <TableCell>{row.name_fr || "-"}</TableCell>
                <TableCell>{row.type}</TableCell>
                <TableCell>{row.glAccount || "-"}</TableCell>
                <TableCell>{row.sortOrder || 0}</TableCell>
                <TableCell>{row.isActive ? "Yes" : "No"}</TableCell>
                <TableCell align="right">
                  <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="flex-end"
                  >
                    <Button
                      size="small"
                      onClick={() => openEdit(row)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => removeExpenseAccount(row.id)}
                    >
                      Delete
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {!loading && (expenseAccounts || []).length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography color="text.secondary">
                    No expense accounts found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Stack>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {formData.id ? "Edit Expense Account" : "Add Expense Account"}
        </DialogTitle>
        <DialogContent>
          <Stack
            spacing={2}
            mt={1}
          >
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              required
            />
            <TextField
              label="Name (French)"
              value={formData.name_fr}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name_fr: e.target.value }))
              }
            />
            <FormControl>
              <InputLabel>Type</InputLabel>
              <Select
                label="Type"
                value={formData.type}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, type: e.target.value }))
                }
              >
                <MenuItem value="expense">Expense</MenuItem>
                <MenuItem value="hours">Hours</MenuItem>
                <MenuItem value="mileage">Mileage</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="GL Account"
              value={formData.glAccount}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, glAccount: e.target.value }))
              }
            />
            <TextField
              label="Sort Order"
              type="number"
              value={formData.sortOrder}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  sortOrder: Number(e.target.value || 0),
                }))
              }
            />
            <Box
              display="flex"
              alignItems="center"
              gap={1}
            >
              <Typography>Active</Typography>
              <Switch
                checked={formData.isActive}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    isActive: e.target.checked,
                  }))
                }
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={onSave}
            disabled={!formData.name.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
};

export default ExpenseAccounts;
